import type { MouseEvent } from "react";

import { resolverCondicao } from "@/shared/components";
import { formatarStatusFinanceiro } from "@/modules/financeiro-obras";
import type { ObraDestinoTransferencia } from "@/modules/financeiro-obras";
import { NO_VALUE } from "../planejamento-controle.constants";
import { formatarData, paraData } from "../planejamento-controle.datas";
import { formatMilhoes } from "../planejamento-controle.format";
import type {
  CelulaDestino,
  ColunaDestino,
  EtapaDestinoTransferenciaProps,
  LinhaEtapaDestino,
} from "./types-etapa-destino-transferencia";

const dataOuTraco = (iso: string | null): string => {
  const data = paraData(iso);
  return data ? formatarData(data) : NO_VALUE;
};

function celula(obra: ObraDestinoTransferencia, coluna: ColunaDestino): CelulaDestino {
  switch (coluna) {
    case "status":
      return { valor: formatarStatusFinanceiro({ codigo: obra.fel }), etiqueta: true };
    case "tipos":
      return {
        valor: obra.tiposObra.map((tipo) => tipo.codigo).join(" · ") || NO_VALUE,
        etiqueta: false,
      };
    case "condicao":
      return { valor: resolverCondicao(obra.condicao)?.label ?? NO_VALUE, etiqueta: false };
    case "inicio":
      return { valor: dataOuTraco(obra.periodo.dataInicio), etiqueta: false };
    case "termino":
      return { valor: dataOuTraco(obra.periodo.dataFim), etiqueta: false };
    case "outlook":
      return { valor: formatMilhoes(obra.outlook), etiqueta: false };
  }
}

export function useEtapaDestinoTransferencia({
  titulo,
  subtitulo,
  aviso,
  ariaLabel,
  obras,
  colunas,
  textos,
  selecionadaId,
  onSelecionar,
  testIdPrefix,
  children,
}: EtapaDestinoTransferenciaProps) {
  const linhas: LinhaEtapaDestino[] = obras.map((obra) => {
    const motivo = obra.disponivel ? null : (obra.motivoIndisponivel ?? textos.indisponivel);
    const situacao =
      motivo === null ? textos.disponivel : (textos.abreviarMotivo?.(motivo) ?? motivo);
    return {
      id: obra.id,
      codigo: obra.codigo,
      disponivel: obra.disponivel,
      selecionada: obra.id === selecionadaId,
      celulas: colunas.map((coluna) => celula(obra, coluna)),
      situacao,
      situacaoTitulo: motivo !== null && situacao !== motivo ? motivo : undefined,
      testIds: {
        linha: `${testIdPrefix}-obra-${obra.id}`,
        radio: `${testIdPrefix}-obra-radio-${obra.id}`,
        situacao: `${testIdPrefix}-obra-situacao-${obra.id}`,
      },
    };
  });

  return {
    titulo,
    subtitulo,
    aviso,
    ariaLabel,
    cabecalhos: colunas.map((coluna) => textos.colunas[coluna]),
    colunaId: textos.colunaId,
    colunaSituacao: textos.colunaSituacao,
    linhas,
    selecionadaId,
    selecionar: (id: string) => onSelecionar(id),
    clicarLinha: (linha: LinhaEtapaDestino, evento: MouseEvent<HTMLElement>) => {
      if (!linha.disponivel) return;
      if ((evento.target as HTMLElement).closest("label, input, button")) return;
      onSelecionar(linha.id);
    },
    testIds: {
      etapa: `${testIdPrefix}-destino`,
      aviso: `${testIdPrefix}-embarcacao`,
      obras: `${testIdPrefix}-obras`,
    },
    children,
  };
}
